import React, { useRef } from "react";
import { RotateCw, Download, Upload, Clock, MapPin, CalendarClock, MapPinned } from "lucide-react";
import LocationScheduleImport from "../../utils/LocationScheduleImport";
import DefaultLocationModal, { defaultLocationLabel } from "../modals/DefaultLocationModal";
import ShiftControlModal from "../shiftControl/ShiftControlModal";
import RemotePunchModal from "../remotePunch/RemotePunchModal";
import ActionButtonGrid, { ActionGridButton } from "../../../../components/ActionButtonGrid";
import { text as shiftControlText } from "../shiftControl/shiftControlText";
import { rpText } from "../../../../components/RemotePunch/remotePunchText";
import templateFile from "../../../../Documents/Attendance_template.xlsx";

const ActionButtons = ({
  theme,
  isRTL,
  t,
  language,
  isRecalculating,
  canRefreshAttendance,
  canExportCsv,
  canExportTemplate,
  canImportCsv,
  canImportLocationSchedule,
  canDefaultLocation,
  canShiftControl = false,
  canRemotePunch = false,
  canScheduleTemplate,
  canApproveOvertime = false,
  overtimeApprovalRequired = false,
  forceRefreshAttendance,
  exportToCSV,
  handleImportFileChange,
  overtimePendingCount = 0,
  overtimeTotalCount = 0,
  onOvertimeClick = () => { },
}) => {
  const isAr = language === "ar";
  const csvInputRef = useRef(null);
  const locationScheduleRef = useRef(null);
  const defaultLocationRef = useRef(null);
  const shiftControlRef = useRef(null);
  const remotePunchRef = useRef(null);

  const handleExportTemplate = () => {
    const link = document.createElement("a");
    link.href = templateFile;
    link.download = "attendance_template.xlsx";
    link.click();
  };

  const showOvertime = overtimeApprovalRequired && canApproveOvertime;

  // Every action the user may run, in display order.
  const actions = [
    canRefreshAttendance && {
      key: "refresh",
      Icon: RotateCw,
      label: isRecalculating ? t.recalculating : t.refresh,
      run: forceRefreshAttendance,
      disabled: isRecalculating,
      spin: isRecalculating,
    },
    canExportCsv && { key: "exportCsv", Icon: Download, label: t.exportCSV, run: exportToCSV },
    canExportTemplate && { key: "exportTemplate", Icon: Download, label: t.exportTemplate, run: handleExportTemplate },
    canImportCsv && {
      key: "importCsv",
      Icon: Upload,
      label: isAr ? "استيراد CSV" : "Import CSV",
      run: () => csvInputRef.current?.click(),
    },
    canImportLocationSchedule && {
      key: "locationSchedule",
      Icon: MapPin,
      label: isAr ? "إدخال جدول المواقع" : "Import Location Schedule",
      run: () => locationScheduleRef.current?.pickFile(),
    },
    canImportLocationSchedule && {
      key: "scheduleTemplate",
      Icon: Download,
      label: isAr ? "نموذج الجدول" : "Schedule Template",
      run: () => locationScheduleRef.current?.downloadTemplate(),
    },
    canDefaultLocation && {
      key: "defaultLocation",
      Icon: MapPin,
      label: defaultLocationLabel(language),
      run: () => defaultLocationRef.current?.open(),
    },
    canShiftControl && {
      key: "shiftControl",
      Icon: CalendarClock,
      label: (shiftControlText[language] || shiftControlText.en).title,
      run: () => shiftControlRef.current?.open(),
    },
    canRemotePunch && {
      key: "remotePunch",
      Icon: MapPinned,
      label: rpText(language).manageButton,
      run: () => remotePunchRef.current?.open(),
    },
    showOvertime && {
      key: "overtime",
      Icon: Clock,
      label: isAr ? "طلبات الإضافي" : "Overtime Requests",
      run: onOvertimeClick,
      badge: overtimePendingCount,
    },
  ].filter(Boolean);

  const csvInput = canImportCsv && (
    <input
      ref={csvInputRef}
      type="file"
      accept=".csv,.xlsx,.xls"
      onChange={handleImportFileChange}
      style={{ display: "none" }}
    />
  );

  // One grid of equal-size buttons on every screen (two columns on phones —
  // see ActionButtonGrid). The self-contained modals keep their own trigger
  // hidden and open through refs from this shared action list.
  return (
    <ActionButtonGrid theme={theme}>
      {csvInput}
      {canImportLocationSchedule && <LocationScheduleImport ref={locationScheduleRef} hideTrigger />}
      {canDefaultLocation && <DefaultLocationModal ref={defaultLocationRef} theme={theme} language={language} isRTL={isRTL} hideTrigger />}
      {canShiftControl && <ShiftControlModal ref={shiftControlRef} theme={theme} language={language} hideTrigger />}
      {canRemotePunch && <RemotePunchModal ref={remotePunchRef} theme={theme} language={language} hideTrigger />}

      {actions.map((action) => (
        <ActionGridButton
          key={action.key}
          theme={theme}
          icon={action.Icon}
          label={action.label}
          onClick={action.run}
          disabled={action.disabled}
          spin={action.spin}
          badge={action.badge}
        />
      ))}
    </ActionButtonGrid>
  );
};

export default ActionButtons;
